import { useRef, useCallback } from "react";

/**
 * Shared hook for metric polling and recording URL preservation.
 * Used by both ConversationDetail and RunResults pages.
 *
 * @param {Function} fetchFn - async function that returns fresh data from API
 * @param {Function} setData - state setter for the data (call or run)
 * @param {Object} options
 * @param {string} options.recordingUrlKey - key for recording URL in data ("recording_url" or "call_recording_url")
 * @param {Function} options.isPending - function(data) => boolean, whether data still needs polling
 * @param {number} options.interval - polling interval in ms (default 5000)
 */
export default function useMetricPolling(fetchFn, setData, options = {}) {
  const {
    recordingUrlKey = "recording_url",
    isPending = () => false,
    interval = 5000,
  } = options;

  const pollRef = useRef(null);

  // Preserve recording URL to prevent audio player remount.
  // Presigned URLs change on every request (different signature) even though
  // the underlying S3 object is the same. Comparing pathnames detects this.
  const preserveRecordingUrl = useCallback((prev, next) => {
    if (!prev || !next) return next;
    const prevUrl = prev[recordingUrlKey];
    const nextUrl = next[recordingUrlKey];
    if (prevUrl && nextUrl) {
      const stripQuery = (u) => {
        try { return new URL(u).pathname; } catch { return u; }
      };
      if (stripQuery(prevUrl) === stripQuery(nextUrl)) {
        return { ...next, [recordingUrlKey]: prevUrl };
      }
    }
    return next;
  }, [recordingUrlKey]);

  const updateData = useCallback((newData) => {
    setData((prev) => preserveRecordingUrl(prev, newData));
  }, [setData, preserveRecordingUrl]);

  // Escape hatch from preserveRecordingUrl. Presigned URLs expire (30 min), and
  // because we always keep the FIRST one to avoid remounting the player, a page
  // left open past the expiry holds a URL that now 403s — the audio player has no
  // way to recover on its own. Fetch fresh data and take the NEW signed URL.
  const refreshRecordingUrl = useCallback(async () => {
    const fresh = await fetchFn();
    if (!fresh?.[recordingUrlKey]) return null;
    setData((prev) => ({ ...(prev || {}), ...fresh }));  // no preserve — that's the point
    return fresh[recordingUrlKey];
  }, [fetchFn, setData, recordingUrlKey]);

  const startPolling = useCallback((initialData) => {
    stopPolling();
    if (!isPending(initialData)) return;

    pollRef.current = setInterval(async () => {
      try {
        const freshData = await fetchFn();
        if (freshData) {
          setData((prev) => preserveRecordingUrl(prev, freshData));
          if (!isPending(freshData)) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }
      } catch {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, interval);
  }, [fetchFn, setData, preserveRecordingUrl, isPending, interval]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Set all metrics to pending state locally (for rerun)
  const setMetricsPending = useCallback((metricKeys = []) => {
    setData((prev) => {
      if (!prev) return prev;
      const setPending = (metrics) =>
        (metrics || []).map((m) => ({
          ...m,
          status: "pending",
          result_data: null,
          error_message: null,
        }));

      const updates = {};
      metricKeys.forEach((key) => {
        if (prev[key]) updates[key] = setPending(prev[key]);
      });

      return { ...prev, ...updates, llm_insights: null };
    });
  }, [setData]);

  return {
    updateData,
    startPolling,
    stopPolling,
    setMetricsPending,
    refreshRecordingUrl,
    pollRef,
  };
}
